import type { HTTPError } from 'ky';

import type { ToastMessage } from '~/components/Toast';
import type { LabelDetailPayload } from '~settings/api/lib/types';
import { HttpStatusCode } from '~settings/api/lib/types';

import type { LabelFormSchema } from './components/LabelForm';

interface CoopErrorResponse {
  detail: string;
  message: string;
}

export const getErrorMessage = async (
  error: HTTPError,
  t: (key: string) => string,
  isCreate = false,
): Promise<ToastMessage> => {
  const status = error?.response?.status as HttpStatusCode;

  switch (status) {
    case HttpStatusCode.BadRequest: {
      const coopError = await parseCoopBadRequest(error);

      return {
        title: t('label.errors.validation.title'),
        description: coopError || t('label.errors.validation.message'),
      };
    }
    case HttpStatusCode.Forbidden: {
      if (isCreate) {
        return {
          title: t('label.errors.forbidden.title'),
          description: t('label.errors.forbidden.createMessage'),
        };
      }

      return {
        title: t('label.errors.forbidden.title'),
        description: t('label.errors.forbidden.editMessage'),
      };
    }
    default:
      if (isCreate) {
        return {
          title: t('label.errors.generic.title'),
          description: t('label.errors.generic.createMessage'),
        };
      }

      return {
        title: t('label.errors.generic.title'),
        description: t('label.errors.generic.editMessage'),
      };
  }
};

export const getLabelPayload = (
  data: LabelFormSchema,
  isActive: boolean,
): LabelDetailPayload => {
  return {
    active: data.active ?? isActive,
    name: data.labelName,
    labelGroups: data.labelGroups?.map((group) => group.value) ?? [],
    weaLabelCode: data.weaLabelCode,
    usLabelCode: data.usLabelCode,
    dealOwner: data.dealOwner,
    dealRegionScope: data.dealRegionScope,
    wmiLabelCode: data.wmiLabelCode,
    marketingCompany: data.marketingCompany,
    repertoireOwner: data.repertoireOwner,
    wbsElementNumber: data.wbsElementNumber,
    dealId: data.dealId,
    scopeId: data.scopeId,
    profitCenter: data.profitCenter,
    pmo: data.pmo,
    gvlCode: data.gvlCode,
    labelPresentation: data.labelPresentation.value,
    distributorId: data.distributorId,
    gpiNumber: data.gpiNumber,
    distributionGroups: data.distributionGroups.map((group) => {
      return {
        ...group,
        countries: group.distributionGroupCountries.map(
          (countries) => countries.value,
        ),
        notes: group.notes || null,
      };
    }),
    facebookReference: data.facebookReference,
    twitterReference: data.twitterReference,
    youtubeReference: data.youtubeReference,
    instagramReference: data.instagramReference,
    website: data.website,
    allowTimedRelease: data.allowTimedRelease ?? false,
    asylum: data.asylum ?? false,
    directPassQc: data.directPassQc ?? false,
    parentId: data.parentLabel?.value || null,
    labelIsrcPrefix: data.labelIsrcPrefix || null,
    digitalProduct: true, // there should be 1 of 2 to pass COOP validation
    physicalProduct: false,
  };
};

async function parseCoopBadRequest(error: HTTPError): Promise<string | null> {
  const data: CoopErrorResponse = await error.response.json();

  return data?.detail;
}

export const getDeleteMessageFromStatus = (status: HttpStatusCode) => {
  if (status === HttpStatusCode.Forbidden) {
    return {
      title: 'label.errors.forbidden.title',
      description: 'label.errors.forbidden.deleteMessage',
    };
  }

  return {
    title: 'label.errors.generic.title',
    description: 'label.errors.generic.deleteMessage',
  };
};
